/**
 * Lockstep Platform SDK for TypeScript
 *
 * (c) 2021-2023 Lockstep, Inc.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 *
 * @author     Lockstep Network <support@lockstep.io>
 * @copyright  2021-2023 Lockstep, Inc.
 * @link       https://github.com/Lockstep-Network/lockstep-sdk-typescript
 */
export class WebhooksClient {
    /**
     * Internal constructor for this client library
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * Retrieves the Webhook specified by this unique identifier.
     *
     * @param id The unique Lockstep Platform ID number of this Webhook
     */
    retrieveWebhook(id) {
        const url = `/api/v1/Webhooks/${id}`;
        return this.client.request("get", url, null, null);
    }
    /**
     * Updates a webhook that matches the specified id with the requested information.
     *
     * The PATCH method allows you to change specific values on the object while leaving other values alone.  As input you should supply a list of field names and new values.  If you do not provide the name of a field, that field will remain unchanged.  This allows you to ensure that you are only updating the specific fields desired.
     *
     * @param id The unique Lockstep Platform ID number of the Webhook to update.
     * @param body A list of changes to apply to this Webhook
     */
    updateWebhook(id, body) {
        const url = `/api/v1/Webhooks/${id}`;
        return this.client.request("patch", url, null, body);
    }
    /**
     * Deletes the Webhook referred to by this unique identifier.
     *
     * @param id The unique Lockstep Platform ID number of the Webhook to delete.
     */
    deleteWebhook(id) {
        const url = `/api/v1/Webhooks/${id}`;
        return this.client.request("delete", url, null, null);
    }
    /**
     * Creates one or more webhooks from a given model.
     *
     * @param body The Webhooks to create
     */
    createWebhooks(body) {
        const url = `/api/v1/Webhooks`;
        return this.client.request("post", url, null, body);
    }
    /**
     * Updates a webhook that matches the specified id with a new client secret.
     *
     * @param id The unique Lockstep Platform ID number of the Webhook to update.
     */
    regenerateClientSecret(id) {
        const url = `/api/v1/Webhooks/${id}/regenerate-client-secret`;
        return this.client.request("patch", url, null, null);
    }
    /**
     * Queries Webhooks for this account using the specified filtering, sorting, and pagination rules requested.
     *
     * More information on querying can be found on the [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight) page on the Lockstep Developer website.
     *
     * @param filter The filter for this query. See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collection: WebhookRules
     * @param order The sort order for this query. See See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageSize The page size for results (default 250, maximum of 500). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     * @param pageNumber The page number for results (default 0). See [Searchlight Query Language](https://developer.lockstep.io/docs/querying-with-searchlight)
     */
    queryWebhooks(filter, include, order, pageSize, pageNumber) {
        const url = `/api/v1/Webhooks/query`;
        const options = {
            params: {
                filter,
                include,
                order,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     *
     * @param webhookId The unique Lockstep Platform ID number of this Webhook
     * @param include To fetch additional data on this object, specify the list of elements to retrieve. Available collection: Records, RequestMessage, ResponseMessage
     * @param filter The filter for this query. See [Azure Query Language](https://docs.microsoft.com/en-us/rest/api/storageservices/querying-tables-and-entities)
     * @param select The selection for this query. Selection is the desired properties of an entity to pull from the set. If a property is not selected, it will either return as null or empty. See [Azure Query Language](https://docs.microsoft.com/en-us/rest/api/storageservices/querying-tables-and-entities)
     * @param pageSize The page size for results (default 250, maximum of 500).
     * @param pageNumber The page number for results (default 0).
     */
    queryWebhookHistory(webhookId, include, filter, select, pageSize, pageNumber) {
        const url = `/api/v1/Webhooks/${webhookId}/history/query`;
        const options = {
            params: {
                include,
                filter,
                select,
                pageSize,
                pageNumber,
            },
        };
        return this.client.request("get", url, options, null);
    }
    /**
     *
     * @param webhookId The unique Lockstep Platform ID number of this Webhook
     * @param webhookHistoryId The unique Lockstep Platform ID number of the Webhook History to be retried. Note: the webhook history supplied must have a isSuccessful status of false to be retried.
     */
    retryFailedWebhookHistory(webhookId, webhookHistoryId) {
        const url = `/api/v1/Webhooks/${webhookId}/history/${webhookHistoryId}/retry`;
        return this.client.request("get", url, null, null);
    }
}
//# sourceMappingURL=WebhooksClient.js.map